import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ArrowRight,
  Users,
  Building,
  Boxes,
  BookOpen,
  Laptop,
  FileText,
  UserSquare,
  Crown,
  ShoppingCart,
  DollarSign,
  CheckCircle2,
  Package,
  Sparkles,
  Target,
  Link as LinkIcon,
  Zap,
  BellRing,
  ClipboardList,
  HardHat,
  GraduationCap,
  Handshake,
  FilePlus,
  ChevronRight,
  Megaphone,
  Images,
  UserPlus,
  BadgeCheck,
} from "lucide-react";
import { SOLUTIONS, QUICK_ACCESS, FOOTER_STATS, HERO_IMAGE } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { fetchActividadFeed } from "../lib/actividad";
import OnboardingNudge from "../components/OnboardingNudge";

const FEED_META = {
  publicacion: { Icon: Megaphone, color: "#2563EB", tint: "#2563EB22" },
  portafolio: { Icon: Images, color: "#16A34A", tint: "#16A34A22" },
  miembro: { Icon: UserPlus, color: "#7E22CE", tint: "#7E22CE22" },
  verificacion: { Icon: BadgeCheck, color: "#059669", tint: "#05966922" },
};

function esNuevo(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 72 * 3600 * 1000;
}

function tiempoRel(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "Hace segundos";
  const m = Math.floor(s / 60);
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h} h`;
  return `Hace ${Math.floor(h / 24)} d`;
}

const QUICK_ICONS = {
  Proyectos: FileText,
  Profesionales: UserSquare,
  Empresas: Building,
  Materiales: Boxes,
  Cursos: BookOpen,
  Software: Laptop,
  Documentos: FileText,
};

function SolutionCard({ s, index }) {
  const soon = s.proximamente;
  const accent = soon ? "#94A3B8" : s.color;         // gris neutro cuando está inactivo
  const accentTint = soon ? "rgba(148,163,184,0.12)" : s.tint;
  return (
    <div
      data-testid={`solution-card-${s.id}`}
      className={`group relative bg-white rounded-2xl border shadow-soft transition-all duration-300 overflow-hidden flex flex-col animate-fade-up ${soon ? "border-slate-200 saturate-0" : "border-slate-200/70 hover:shadow-lifted hover:-translate-y-1.5"}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Top accent bar */}
      <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}99)` }} />

      {/* Image header band */}
      <div
        className="relative h-40 overflow-hidden flex items-center justify-center"
        style={{ background: `radial-gradient(120% 120% at 50% 0%, #FFFFFF 40%, ${accentTint} 100%)` }}
      >
        <img
          src={s.iconImage}
          alt={s.title}
          loading="lazy"
          className={`max-h-[85%] max-w-[86%] object-contain transition-transform duration-500 ${soon ? "grayscale opacity-40" : "drop-shadow-md group-hover:scale-[1.06]"}`}
          data-testid={`solution-icon-${s.id}`}
        />
        {/* Number badge over image */}
        <div
          className="absolute top-3 left-3 text-white font-black text-xs px-2.5 py-1 rounded-lg shadow-md ring-1 ring-white/40"
          style={{ backgroundColor: accent }}
        >
          {s.number}
        </div>
        {soon && (
          <div className="absolute top-3 right-12 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-700 text-white shadow-sm" data-testid={`solution-soon-${s.id}`}>
            Próximamente
          </div>
        )}
        {/* Solution icon chip */}
        <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/85 backdrop-blur flex items-center justify-center shadow-sm" style={{ color: accent }}>
          <s.Icon size={16} />
        </div>
      </div>

      <div className="relative p-5 flex flex-col flex-1 border-t border-slate-100">
        <h3 className={`text-[15px] font-extrabold tracking-wide uppercase leading-tight ${soon ? "text-slate-500" : "text-slate-900"}`}>
          {s.tagline}
        </h3>
        <div className="mt-2 h-0.5 w-9 rounded-full" style={{ backgroundColor: accent }} />
        {s.subtitle && (
          <p className={`text-[13px] font-semibold mt-3 ${soon ? "text-slate-500" : "text-slate-700"}`}>{s.subtitle}</p>
        )}
        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
          {s.description}
        </p>

        <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
          {s.features.map((f) => (
            <li key={f} className="flex items-center gap-2.5">
              <span
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: accentTint, color: accent }}
              >
                <CheckCircle2 size={11} />
              </span>
              <span className={`font-medium ${soon ? "text-slate-500" : ""}`}>{f}</span>
            </li>
          ))}
        </ul>

        {soon ? (
          <button
            type="button"
            disabled
            aria-disabled="true"
            data-testid={`solution-cta-${s.id}`}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-slate-500 bg-slate-200 cursor-not-allowed select-none"
          >
            Próximamente
          </button>
        ) : (
          <Link
            to={s.path}
            data-testid={`solution-cta-${s.id}`}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 hover:gap-3 shadow-sm"
            style={{ backgroundColor: s.color }}
          >
            {s.cta} <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </div>
  );
}

function FlowStep({ Icon, label, color, delay }) {
  return (
    <div className="flex flex-col items-center text-center flex-shrink-0 w-[110px]" style={{ animationDelay: `${delay}ms` }}>
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-soft border border-slate-100 bg-white"
        style={{ color }}
      >
        <Icon size={22} />
      </div>
      <p className="mt-3 text-[12px] font-semibold text-slate-700 leading-tight">
        {label}
      </p>
    </div>
  );
}

function EcosystemFlow() {
  const steps = [
    { Icon: FilePlus, label: "Publicas tu proyecto", color: "#1E3A8A" },
    { Icon: Sparkles, label: "IA analiza y sugiere", color: "#7E22CE" },
    { Icon: UserSquare, label: "Profesionales reciben alerta", color: "#DC2626" },
    { Icon: ClipboardList, label: "Cotizaciones y propuestas", color: "#16A34A" },
    { Icon: ShoppingCart, label: "Marketplace provee recursos", color: "#D97706" },
    { Icon: GraduationCap, label: "Academia recomienda", color: "#EA580C" },
  ];
  return (
    <section className="mt-16" data-testid="ecosystem-flow">
      <h2 className="text-center text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
        UN ECOSISTEMA CONECTADO QUE TRABAJA POR TI
      </h2>
      <p className="text-center text-sm text-slate-500 mt-1">
        Cada paso alimenta al siguiente. Una sola cuenta, un solo flujo, un solo Ecosistema.
      </p>

      <div className="mt-8 bg-white rounded-2xl border border-slate-100 shadow-soft p-6 lg:p-8 overflow-x-auto cl-scroll-light">
        <div className="flex items-center justify-between min-w-[900px] gap-2">
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <FlowStep {...step} delay={i * 80} />
              {i < steps.length - 1 && (
                <div className="flex-1 flex items-center min-w-[24px]">
                  <div className="flow-arrow flex-1" />
                  <ChevronRight size={16} className="text-slate-400 -ml-1" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function RightPanel({ user }) {
  const [eventos, setEventos] = useState([]);

  useEffect(() => {
    let cancel = false;
    fetchActividadFeed(6)
      .then((d) => { if (!cancel) setEventos(d.eventos || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  return (
    <aside className="space-y-5" data-testid="right-panel">
      {/* Progress */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="right-panel-progress">
        <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Tu progreso</p>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-slate-600">Perfil completo</p>
          <p className="text-sm font-bold text-slate-900">85%</p>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-red" style={{ width: "85%" }} />
        </div>

        <div className="grid grid-cols-3 gap-2 mt-5 text-center">
          <div className="bg-slate-50 rounded-xl p-2.5">
            <p className="text-lg font-extrabold text-slate-900">12</p>
            <p className="text-[10px] font-semibold text-slate-500 leading-tight">Proyectos publicados</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-2.5">
            <p className="text-lg font-extrabold text-slate-900">8</p>
            <p className="text-[10px] font-semibold text-slate-500 leading-tight">Cotizaciones recibidas</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-2.5">
            <p className="text-lg font-extrabold text-slate-900">3</p>
            <p className="text-[10px] font-semibold text-slate-500 leading-tight">Proyectos en proceso</p>
          </div>
        </div>
      </div>

      {/* Premium */}
      <div
        data-testid="right-panel-premium"
        className="relative overflow-hidden rounded-2xl p-5 text-white shadow-soft"
        style={{ background: "linear-gradient(135deg, #0B1E36 0%, #1E3A8A 100%)" }}
      >
        <div
          aria-hidden
          className="absolute -right-8 -top-8 w-28 h-28 rounded-full opacity-30 blur-2xl"
          style={{ background: "#FBBF24" }}
        />
        <div className="flex items-center gap-2 mb-2">
          <Crown size={18} className="text-brand-gold" />
          <p className="font-semibold text-sm">¡Hazte Premium!</p>
        </div>
        <p className="text-slate-300 text-xs leading-relaxed">
          Obtén más visibilidad y beneficios exclusivos en todo el Ecosistema.
        </p>
        <Link
          to="/planes"
          className="mt-4 inline-flex items-center gap-1.5 bg-brand-gold hover:bg-yellow-300 text-[#0B1E36] font-semibold text-xs px-3.5 py-2 rounded-lg transition-colors"
          data-testid="right-panel-premium-cta"
        >
          Ver planes <ChevronRight size={14} />
        </Link>
      </div>

      {/* Actividad reciente (real) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="right-panel-activity">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Trabajos y actividad reciente</p>
          <Link to="/actividad" className="text-xs font-semibold text-brand-blue hover:text-brand-red">
            Ver todas
          </Link>
        </div>
        <ul className="mt-4 space-y-4">
          {eventos.length === 0 ? (
            <li className="text-xs text-slate-400">Cargando actividad del ecosistema…</li>
          ) : (
            eventos.map((ev) => {
              const meta = FEED_META[ev.tipo] || FEED_META.publicacion;
              const Icon = meta.Icon;
              const nuevo = esNuevo(ev.fecha);
              return (
                <li key={ev.id}>
                  <Link to={ev.enlace} className="flex gap-3 items-start group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: meta.tint, color: meta.color }}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 leading-tight truncate group-hover:text-brand-blue">
                        {ev.actor_nombre}
                        {nuevo && (
                          <span className="ml-1.5 align-middle inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-brand-red text-white px-1.5 py-0.5 rounded-full" data-testid="badge-nuevo">
                            Nuevo
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {ev.descripcion}{ev.titulo ? `: ${ev.titulo}` : ""}
                      </p>
                    </div>
                    <p className="text-[10px] text-slate-400 font-medium flex-shrink-0 mt-1">{tiempoRel(ev.fecha)}</p>
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      </div>

      {/* Brand card */}
      <div
        data-testid="right-panel-brand"
        className="relative overflow-hidden rounded-2xl p-6 text-white shadow-soft"
        style={{ background: "linear-gradient(135deg, #0B1E36 0%, #1E3A8A 100%)" }}
      >
        <p className="text-xs uppercase tracking-widest font-bold text-brand-gold/90">
          ConstruLink
        </p>
        <p className="mt-2 text-lg font-extrabold leading-tight">
          Donde la construcción y la tecnología construyen el <span className="text-brand-gold">futuro</span>.
        </p>
        <Link
          to="/conexion"
          className="mt-4 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors w-full justify-center"
        >
          Únete al Ecosistema <ArrowRight size={16} />
        </Link>
        <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-slate-300">
          <div className="flex items-center gap-1.5">
            <Target size={12} className="text-brand-red" />
            <span>Enfocados en soluciones</span>
          </div>
          <div className="flex items-center gap-1.5">
            <LinkIcon size={12} className="text-brand-blue" />
            <span>Todo conectado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap size={12} className="text-brand-gold" />
            <span>Energía + Eficiencia</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function HomeDashboard() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");

  return (
    <div className="px-5 md:px-8 py-8" data-testid="home-dashboard">
      <OnboardingNudge />
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-8">
        <div className="min-w-0">
          {/* Hero */}
          <section
            className="relative rounded-3xl overflow-hidden border border-slate-200/70 shadow-lifted bg-white"
            data-testid="hero-section"
          >
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[180px] overflow-hidden" data-testid="hero-ecosistema-grid">
              <div className="p-8 md:p-10 lg:p-12 relative z-10 flex flex-col justify-center">
                <span className="inline-flex items-center gap-2 self-start px-3.5 py-1.5 rounded-full bg-gradient-to-r from-brand-red/15 to-brand-red/5 text-brand-red text-[11px] font-bold uppercase tracking-widest ring-1 ring-brand-red/20">
                  <Sparkles size={12} /> Ecosistema ConstruLink
                </span>
                <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]">
                  CONECTAMOS TODA LA INDUSTRIA
                  <br />
                  <span className="text-brand-blue">DE LA </span>
                  <span className="text-brand-red">CONSTRUCCIÓN</span>
                </h1>
                <p className="mt-4 text-base md:text-lg text-slate-600 font-medium">
                  <span className="text-brand-blue font-semibold">Una plataforma.</span>{" "}
                  <span className="text-slate-800 font-semibold">Cinco soluciones.</span>{" "}
                  <span className="text-brand-red font-semibold">Una sola experiencia.</span>
                </p>

                <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  {[
                    { label: "Personas", color: "#1E3A8A" },
                    { label: "Empresas", color: "#0891B2" },
                    { label: "Tecnología", color: "#7E22CE" },
                    { label: "Conocimiento", color: "#EA580C" },
                    { label: "Recursos", color: "#D97706" },
                  ].map((tag) => (
                    <span key={tag.label} className="inline-flex items-center gap-1.5 text-slate-700 font-medium">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative h-64 lg:h-auto min-h-[320px] overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="Ciudad moderna en construcción al atardecer"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[6000ms] ease-out hover:scale-105"
                  data-testid="hero-image"
                />
                {/* Degradados de profundidad + acento de marca */}
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/45 to-transparent lg:via-white/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent lg:from-transparent" />
                <div aria-hidden className="absolute -bottom-px left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
              </div>
              {/* Recorte visual al ~40% de altura: gradiente de cierre (contenido íntegro, solo oculto visualmente) */}
              <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent z-20" />
            </div>

            {/* Search bar */}
            <div className="relative z-10 px-6 md:px-10 pb-9 -mt-4">
              <div
                className="flex items-center w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-lifted border border-slate-200 focus-within:ring-2 focus-within:ring-brand-red/40 transition-all overflow-hidden"
                data-testid="global-search"
              >
                <div className="pl-5 text-slate-400">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  data-testid="hero-search-input"
                  placeholder="Buscar proyectos, profesionales, empresas, materiales..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 px-4 py-4 text-base text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
                />
                <button
                  data-testid="hero-search-button"
                  className="bg-brand-red hover:bg-brand-red-hover text-white py-4 px-6 md:px-8 font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Search size={18} />
                  <span className="hidden sm:inline">Buscar</span>
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {QUICK_ACCESS.map((qa) => {
                  const Icon = QUICK_ICONS[qa.label] || FileText;
                  return (
                    <button
                      key={qa.label}
                      data-testid={`quick-access-${qa.label.toLowerCase()}`}
                      className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors shadow-sm"
                    >
                      <Icon size={14} style={{ color: qa.color }} />
                      {qa.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* 5 Soluciones */}
          <section className="mt-14" data-testid="solutions-section">
            <div className="text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">
                Ecosistema
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                LAS 5 SOLUCIONES DE CONSTRULINK
              </h2>
              <p className="mt-2 text-sm text-slate-500 max-w-2xl mx-auto">
                Cinco soluciones especializadas que pueden usarse por separado, pero fueron diseñadas para trabajar de forma integrada bajo una sola identidad digital.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
              {SOLUTIONS.map((s, i) => (
                <SolutionCard key={s.id} s={s} index={i} />
              ))}
            </div>
          </section>

          {/* Ecosystem flow */}
          <EcosystemFlow />

          {/* Footer stats */}
          <section className="mt-16" data-testid="footer-stats">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {FOOTER_STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p
                    className="text-2xl md:text-3xl font-extrabold tracking-tight"
                    style={{ color: stat.color }}
                  >
                    {stat.value}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide leading-tight">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">
                ConstruLink
              </p>
              <p className="mt-1 text-slate-600 text-sm">
                <span className="text-brand-blue font-semibold">Una plataforma.</span>{" "}
                <span className="text-slate-800 font-semibold">Cinco soluciones.</span>{" "}
                <span className="text-brand-red font-semibold">Una sola experiencia.</span>
              </p>
              <p className="mt-4 text-[11px] text-slate-400">
                © {new Date().getFullYear()} ConstruLink · Plataforma Integral para la Industria de la Construcción
              </p>
            </div>
          </section>
        </div>

        {/* Right column */}
        <div className="xl:sticky xl:top-24 self-start">
          <RightPanel user={user} />
        </div>
      </div>
    </div>
  );
}
